import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  SpectrumPanel,
  WaveformPanel,
  SpectrogramPanel,
  ComparisonOverlay,
  BearingFreqTracker,
  StatusPill,
  SecondaryButton,
  TextButton,
  ScalarTrack,
  FFTThumbnailStrip,
  AnnotationStrip,
  BaselinePicker,
  FaultFrequencyEditor,
  loadCustomMarkers,
  saveCustomMarkers,
  AssetPhoto,
  type CustomFaultMarker,
  type StatusLevel,
} from '@/lib';
import {
  LinkedCursorProvider,
  useLinkedCursor,
} from '@/lib/charts';
import { useKeyboardMap } from '@/app/useKeyboardMap';
import { orgRoute } from '@/app/org-route';
import { saveSession } from '@/screens/S5bSessions/storage';
import { buildFixture, listAssetOptions, KNOWN_ASSET_IDS, photoForAsset, getAssetMeta, type AssetOption } from './fixtures';
import {
  useScalarTrends,
  useFftThumbnails,
  useAnnotations,
  useSpectrogram,
  useSpectrum,
  useWaveform,
} from './queries';

// Severity-tone text colors for the FFT history selection indicator.
// Mirrors the dot/sparkline tones used elsewhere on this screen so the
// "showing critical" label reads the same as the strip's tinted bars.
const TONE_TEXT: Record<StatusLevel, string> = {
  critical: 'text-critical',
  warn: 'text-warn-fg',
  watch: 'text-watch-fg',
  healthy: 'text-healthy-fg',
  info: 'text-info',
};

export type S5SignalAnalysisProps = {
  /** Selected asset id from the URL search param. When unset the
   *  workspace picks the first asset in the catalog so a fresh
   *  /analyze visit still renders something useful. */
  asset?: string;
  /** Cursor time (ms since epoch). Currently informational; future
   *  iterations will drive shared LinkedCursor state across panels. */
  t?: number;
  /** Selected sensor IDs from the search-params layer. */
  sensors?: string[];
  /** Selected baseline capture identifier. */
  baseline?: string;
  /** Optional compare-asset ids — when present, ScalarTrack overlays
   *  the compared asset's series as a dashed line. Spec allows up to
   *  3 total assets; the first id wins until the multi-overlay UI
   *  lands. */
  compare?: string[];
};

/**
 * S5 Signal Analysis. Composes Phase 2 (3/4/5/n) charts:
 *   - WaveformPanel (top): raw time-domain
 *   - SpectrumPanel (middle): FFT with fault-frequency markers
 *   - SpectrogramPanel (bottom): time × frequency heatmap
 * Side rail:
 *   - ComparisonOverlay: current vs baseline spectrum
 *   - BearingFreqTracker: per-fault-frequency trends
 *
 * The fixture data is synthesized client-side until the
 * sensor-graph-service waveform/FFT endpoint ships. Layout and
 * interactions are wired against the real chart components so
 * swapping in live data later is a single-file change.
 */
export function S5SignalAnalysis({ asset, t, sensors, baseline, compare }: S5SignalAnalysisProps) {
  // LinkedCursor (ADR 0003): one cursor controller for the whole
  // workspace. Initial value comes from the URL ?t= search param;
  // SyncCursorToUrl writes back on change so the workspace is
  // deep-linkable.
  return (
    <LinkedCursorProvider initialT={t ? t / 1000 : null} tickSeconds={60}>
      <S5Body asset={asset} sensors={sensors} baseline={baseline} compare={compare} />
    </LinkedCursorProvider>
  );
}

function S5Body({
  asset,
  sensors,
  baseline,
  compare,
}: {
  asset?: string;
  sensors?: string[];
  baseline?: string;
  compare?: string[];
}) {
  // Active asset: URL param wins; falls back to first known asset
  // so a fresh /analyze visit still renders something useful.
  const activeAssetId = asset && KNOWN_ASSET_IDS.includes(asset) ? asset : KNOWN_ASSET_IDS[0];
  const data = useMemo(() => buildFixture(activeAssetId), [activeAssetId]);
  const options = useMemo(() => listAssetOptions(), []);
  // Scalars: first migration to the OpenAPI surface. While the
  // request is in flight (or fails), fall back to the inline fixture
  // so the strip never blank-flashes. The MSW handler currently
  // returns the same data buildFixture produces; once the real
  // backend lands, the only thing that changes is the URL.
  const scalarsQuery = useScalarTrends(activeAssetId);
  const liveScalars = useMemo(() => {
    if (!scalarsQuery.data) return null;
    return (scalarsQuery.data.series ?? []).map((s) => ({
      metric: s.metric,
      unit: s.unit,
      points: (s.points ?? []).map((p) => ({ t: Math.floor(Date.parse(p.ts) / 1000), v: p.value })),
      last: s.last,
    }));
  }, [scalarsQuery.data]);
  const activeScalars = liveScalars ?? data.scalars;
  // Custom markers are operator-defined per-asset overrides loaded
  // from localStorage. Editor state lives at the screen level so the
  // Save/Cancel buttons can be mutated from a child surface.
  const [customMarkers, setCustomMarkers] = useState<CustomFaultMarker[]>(() =>
    loadCustomMarkers(activeAssetId),
  );
  // Re-load when the operator switches assets so the editor reflects
  // the new asset's marker list (not a stale one from the previous).
  useEffect(() => {
    setCustomMarkers(loadCustomMarkers(activeAssetId));
  }, [activeAssetId]);
  const [editorOpen, setEditorOpen] = useState(false);

  const commitCustomMarkers = (next: CustomFaultMarker[]) => {
    setCustomMarkers(next);
    saveCustomMarkers(activeAssetId, next);
  };

  const trackerRows = useMemo(() => {
    // Built-in markers from the asset's kind-aware fixture.
    const builtIn = data.bearingFaultMarkers.map((m) => ({
      label: m.label,
      hz: `${m.hz} Hz`,
      series: m.series,
      severity: (m.tone === 'critical' ? 'critical'
        : m.tone === 'warn' ? 'warn'
        : m.tone === 'watch' ? 'watch'
        : 'info') as StatusLevel,
      rising: m.trend === 'rising',
      amplitude: `${m.amplitude.toFixed(2)} g`,
      ratio: `${m.ratio.toFixed(1)}×`,
    }));
    // Custom markers from localStorage. Amplitude + ratio aren't
    // measured (no FFT to read against), so we render dashes. The
    // operator's reason for adding a custom marker is to *see* the
    // line on the spectrum — the table row carries the same Hz so
    // the marker is still wired into the spectrum panel below.
    const custom = customMarkers.map((m) => ({
      label: m.label,
      hz: `${m.hz} Hz`,
      series: [] as number[],
      severity: m.tone as StatusLevel,
      rising: false,
      amplitude: '—',
      ratio: '—',
    }));
    return [...builtIn, ...custom];
  }, [data.bearingFaultMarkers, customMarkers]);

  // Merge built-in + custom markers for the SpectrumPanel below —
  // both are drawn as vertical fault lines.
  const allSpectrumMarkers = useMemo(() => {
    return [
      ...data.bearingFaultMarkers,
      ...customMarkers.map((m) => ({
        hz: m.hz,
        label: m.label,
        tone: m.tone,
      })),
    ];
  }, [data.bearingFaultMarkers, customMarkers]);

  const cursor = useLinkedCursor();
  // useRouter returns null when rendered outside a RouterProvider
  // (tests + Storybook). Guard so the cursor controls still work
  // without a router; URL sync only runs in the live app.
  const router = safeUseRouter();

  // Sensor selection: URL search-param drives which sensors are active.
  // When the URL has no `sensors`, default to ALL sensors so the operator
  // sees full coverage on first load. The chip group below mutates this.
  const activeSensorIds = useMemo(() => {
    if (sensors && sensors.length > 0) {
      // Intersect with the asset's sensor catalog so a stale URL from
      // a different asset doesn't refer to non-existent sensor ids.
      const known = new Set(data.sensors.map((s) => s.id));
      const filtered = sensors.filter((id) => known.has(id));
      return filtered.length > 0 ? filtered : data.sensors.map((s) => s.id);
    }
    return data.sensors.map((s) => s.id);
  }, [sensors, data.sensors]);

  const primarySensor =
    data.sensors.find((s) => s.id === activeSensorIds[0]) ?? data.sensors[0];

  // Range for the three sensor-graph queries below. Trailing 14d
  // matches what the FFT history strip + scalar trends already showed
  // before the cutover, so the demo's visual cadence is unchanged.
  // Memoised on the day, not on every render, so React-Query's cache
  // key stays stable across re-renders that don't cross midnight.
  // Day index — when this crosses (midnight UTC), the range memo
  // re-computes so the trailing-14d window walks forward. Computing
  // the index here (rather than inline in the deps array) sidesteps
  // exhaustive-deps style complaints from the lint config.
  const dayIndex = Math.floor(Date.now() / 86_400_000);
  const queryRange = useMemo(() => {
    const now = Date.now();
    return {
      from: new Date(now - 14 * 86_400_000).toISOString(),
      to: new Date(now).toISOString(),
    };
  }, [dayIndex]);

  const thumbnailsQuery = useFftThumbnails(primarySensor.id, queryRange);
  const annotationsQuery = useAnnotations(primarySensor.id, queryRange);
  const spectrogramQuery = useSpectrogram(primarySensor.id, queryRange);
  // Spectrum + waveform are cursor-keyed. When the operator hasn't
  // pinned a moment, fall back to "now" so the panel always shows
  // something meaningful. The MSW handler accepts any ISO timestamp;
  // the future Go handler will pick the burst nearest `at` within
  // its tolerance window.
  const spectrumAt = useMemo(() => {
    if (cursor.state.t != null) {
      return new Date(cursor.state.t * 1000).toISOString();
    }
    return new Date().toISOString();
  }, [cursor.state.t]);
  const spectrumQuery = useSpectrum(primarySensor.id, spectrumAt);
  // Waveform captureId convention matches the MSW resolver:
  // `cap_<assetId>_<unix-seconds>` so different cursor pins fetch
  // different captures even though the demo body is the same.
  const waveformCaptureId = useMemo(() => {
    const ts = cursor.state.t != null
      ? Math.floor(cursor.state.t)
      : Math.floor(Date.now() / 1000);
    return `cap_${activeAssetId}_${ts}`;
  }, [activeAssetId, cursor.state.t]);
  const waveformQuery = useWaveform(waveformCaptureId);

  // Aggregate per-query error state. Each panel still falls back to
  // the synthetic fixture when its query is pending or failed (good
  // for offline demos), but a top-of-screen banner surfaces *which*
  // queries failed so operators know the signal they're looking at
  // is stale — not silently masked as live.
  const queryErrors = useMemo<string[]>(() => {
    const out: string[] = [];
    if (scalarsQuery.isError) out.push('scalars');
    if (thumbnailsQuery.isError) out.push('thumbnails');
    if (annotationsQuery.isError) out.push('annotations');
    if (spectrogramQuery.isError) out.push('spectrogram');
    if (spectrumQuery.isError) out.push('spectrum');
    if (waveformQuery.isError) out.push('waveform');
    return out;
  }, [
    scalarsQuery.isError,
    thumbnailsQuery.isError,
    annotationsQuery.isError,
    spectrogramQuery.isError,
    spectrumQuery.isError,
    waveformQuery.isError,
  ]);

  // Live FFT thumbnails (ts string → Date) with fixture fallback while
  // the query is in flight. Mapping is the cheapest part of the
  // migration — when the real backend serves the same shape, this
  // hook is unchanged.
  const liveThumbnails = useMemo(() => {
    if (!thumbnailsQuery.data) return null;
    return (thumbnailsQuery.data.thumbnails ?? []).map((t) => ({
      at: new Date(t.ts),
      bins: t.bins,
      severity: t.severity,
    }));
  }, [thumbnailsQuery.data]);
  const activeThumbnails = liveThumbnails ?? data.fftThumbnails;

  // Live annotations (ts string → atSec).
  const liveAnnotations = useMemo(() => {
    if (!annotationsQuery.data) return null;
    return (annotationsQuery.data.annotations ?? []).map((a) => ({
      atSec: Math.floor(Date.parse(a.ts) / 1000),
      label: a.label,
      tone: a.tone,
    }));
  }, [annotationsQuery.data]);
  const activeAnnotations = liveAnnotations ?? data.annotations;

  // Live spectrogram — grid is the same number[][] shape SpectrogramPanel
  // already consumes. Duration is derived from the live `from/to` so
  // the time axis on the heatmap matches the query range, not the
  // fixture's static 12s window.
  const liveSpectrogram = useMemo(() => {
    if (!spectrogramQuery.data) return null;
    const fromMs = Date.parse(spectrogramQuery.data.from);
    const toMs = Date.parse(spectrogramQuery.data.to);
    return {
      grid: spectrogramQuery.data.grid ?? [],
      maxHz: spectrogramQuery.data.nyquistHz,
      durationSec: Math.max(1, Math.round((toMs - fromMs) / 1000)),
    };
  }, [spectrogramQuery.data]);

  // Live spectrum — magnitudes → amplitudes, nyquistHz → maxHz. The
  // SpectrumPanel still owns markers + harmonics + peak detection
  // client-side, so the wire shape only needs to provide the raw
  // magnitude array + the band.
  const liveSpectrum = useMemo(() => {
    if (!spectrumQuery.data) return null;
    return {
      amplitudes: spectrumQuery.data.magnitudes ?? [],
      maxHz: spectrumQuery.data.nyquistHz,
    };
  }, [spectrumQuery.data]);

  // Live waveform — samples + sampleRateHz become the WaveformPanel
  // inputs verbatim. Demo MSW returns the same synthetic burst for
  // every captureId; the real backend will return per-capture bursts.
  const liveWaveform = useMemo(() => {
    if (!waveformQuery.data) return null;
    return {
      samples: waveformQuery.data.samples ?? [],
      sampleRate: waveformQuery.data.sampleRateHz,
    };
  }, [waveformQuery.data]);

  // Which thumbnail is currently driving the panels below. When no
  // cursor is pinned we default to the latest capture; clicking a
  // thumbnail picks the nearest match (within ~30 min) so the strip
  // label and the panel data both agree on "we're showing this
  // capture, not some unrelated moment".
  const selectedThumbnail = useMemo(() => {
    if (!activeThumbnails.length) return null;
    const cursorMs = cursor.state.t != null ? cursor.state.t * 1000 : null;
    if (cursorMs == null) {
      return activeThumbnails.reduce(
        (latest, t) => (t.at > latest.at ? t : latest),
        activeThumbnails[0],
      );
    }
    let best = activeThumbnails[0];
    let bestDelta = Math.abs(best.at.getTime() - cursorMs);
    for (const t of activeThumbnails) {
      const d = Math.abs(t.at.getTime() - cursorMs);
      if (d < bestDelta) {
        best = t;
        bestDelta = d;
      }
    }
    return best;
  }, [activeThumbnails, cursor.state.t]);

  const setSensors = (next: string[]) => {
    if (!router) return;
    router.navigate({
      to: orgRoute('/analyze'),
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        sensors:
          next.length === data.sensors.length || next.length === 0
            ? undefined
            : next,
      }),
      replace: true,
    });
  };

  // Baseline: URL ?baseline=<id>. The fixture seeds 3 captures per
  // asset. ComparisonOverlay below reacts to the selection — for
  // now the synthetic baseline curve is the same regardless of
  // capture id, but the picker + URL wiring is the real interface
  // the future endpoint plugs into.
  const setBaseline = (next: string | null) => {
    if (!router) return;
    router.navigate({
      to: orgRoute('/analyze'),
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        baseline: next ?? undefined,
      }),
      replace: true,
    });
  };
  const captureBaselineNow = () => {
    // The future implementation POSTs the current FFT to the
    // baseline-capture endpoint; until then we surface a status
    // message so the demo behaves like the click did something.
    setSaveMsg('Baseline capture queued');
    window.setTimeout(() => setSaveMsg(null), 3000);
  };
  // Resolve URL ?baseline= to a capture id known to the asset. A
  // stale id (left over from a different asset) falls back to null.
  const activeBaselineId = baseline && data.baselineCaptures.some((c) => c.id === baseline)
    ? baseline
    : null;

  // Compare-asset overlay. The spec allows up to 3 assets; the demo
  // implementation supports the first compare id (the data shape is
  // already n-ary, the UI extension is straightforward).
  const compareAssetId = compare && compare.length > 0 && KNOWN_ASSET_IDS.includes(compare[0])
    ? compare[0]
    : null;
  const compareData = useMemo(
    () => (compareAssetId && compareAssetId !== activeAssetId ? buildFixture(compareAssetId) : null),
    [compareAssetId, activeAssetId],
  );
  const setCompare = (nextId: string | null) => {
    if (!router) return;
    router.navigate({
      to: orgRoute('/analyze'),
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        compare: nextId ? [nextId] : undefined,
      }),
      replace: true,
    });
  };

  // Keyboard shortcuts: [ / ] step the cursor, . pins it to now.
  // Matches the documented entries in GLOBAL_SHORTCUTS so the
  // shortcut sheet's promise is now a real behavior.
  const shortcuts = useMemo(
    () => ({
      '[': {
        description: 'Step the LinkedCursor back one tick',
        handler: () => cursor.step(-1),
      },
      ']': {
        description: 'Step the LinkedCursor forward one tick',
        handler: () => cursor.step(1),
      },
      '.': {
        description: 'Pin the current moment to LinkedCursor',
        handler: () => cursor.setT(Math.round(Date.now() / 1000)),
      },
    }),
    [cursor],
  );
  useKeyboardMap(shortcuts);

  // Sync cursor → URL search param `t` (ms since epoch). Skipping
  // initial null avoids overwriting a fresh ?t= from another screen
  // before the user has interacted.
  useEffect(() => {
    if (cursor.state.t == null) return;
    if (!router) return;
    const ms = Math.round(cursor.state.t * 1000);
    router.navigate({
      to: orgRoute('/analyze'),
      search: (prev: Record<string, unknown>) => ({ ...prev, t: ms }),
      replace: true,
    });
  }, [cursor.state.t, router]);

  const cursorLabel = cursor.state.t == null
    ? 'no cursor'
    : new Date(cursor.state.t * 1000).toISOString().slice(11, 19);

  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  // Focus state: clicking a row in BearingFreqTracker zooms the
  // SpectrumPanel to ± focusWindowHz around that frequency. Null
  // clears the zoom back to full range.
  const [focusedFault, setFocusedFault] = useState<{
    hz: number;
    label: string;
  } | null>(null);
  const handleSaveSession = () => {
    const defaultName = `${data.assetName} · ${cursorLabel}`;
    const name = window.prompt('Name this session', defaultName);
    if (!name) return;
    const saved = saveSession({
      name,
      assetId: activeAssetId,
      sensors: sensors ?? [data.sensorId],
      baseline,
      t: cursor.state.t != null ? Math.round(cursor.state.t * 1000) : undefined,
    });
    setSaveMsg(`Saved “${saved.name}”`);
    window.setTimeout(() => setSaveMsg(null), 3000);
  };

  // chooseAsset writes the new id to the URL search param so the
  // whole workspace re-renders against that asset's fixture. URL
  // is the source of truth; component state follows.
  const chooseAsset = (nextId: string) => {
    if (!router) return;
    if (nextId === activeAssetId) return;
    router.navigate({
      to: orgRoute('/analyze'),
      // Reset cursor + sensors/baseline when the asset changes —
      // those are asset-scoped and stale on switch. Keep `pins` so
      // saved layout preferences persist.
      search: (prev: Record<string, unknown>) => ({ ...prev, asset: nextId, t: undefined, sensors: undefined, baseline: undefined }),
    });
  };

  // Cursor → fraction of the visible window for chart overlay.
  // The fixture's spectrogram window is `data.spectrogramDurationSec`
  // seconds wide, anchored at "now"; the waveform window is much
  // shorter (~80ms) — both treat the cursor as a relative position
  // within their own window. Modulo so a cursor far from "now" still
  // renders somewhere meaningful instead of clipping off-screen.
  const cursorFraction =
    cursor.state.t == null
      ? null
      : ((Math.abs(cursor.state.t) % data.spectrogramDurationSec) /
          data.spectrogramDurationSec);

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-start justify-between gap-sp-4">
        <div className="flex min-w-0 flex-1 items-center gap-sp-3">
          <AssetPhoto src={photoForAsset(activeAssetId)} alt={data.assetName} size="header" />
          <div className="min-w-0 flex-1">
            <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Signal analysis
            </p>
            <div className="mt-sp-1">
              <AssetPicker
                options={options}
                activeId={activeAssetId}
                activeName={data.assetName}
                onChoose={chooseAsset}
              />
            </div>
            <p className="mt-sp-2 text-fs-meta text-gray-700">
              <span className="font-mono">{activeAssetId}</span> ·{' '}
              <span title={`Upload cadence: every ${Math.round(primarySensor.uploadEverySec / 60)} min`}>
                {primarySensor.name}
              </span>
              {' · '}
              cadence <span className="font-mono">{Math.round(primarySensor.uploadEverySec / 60)}m</span>
              {' · cursor '}
              <span className="font-mono">{cursorLabel}</span>
              {baseline ? ` · baseline ${baseline}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-sp-2">
          <CursorControls />
          <StatusPill level="warn" label="ACTIVE FAULT" />
          <SecondaryButton label="Save session" onClick={handleSaveSession} />
          {saveMsg ? (
            <span role="status" className="text-fs-meta text-healthy-fg">
              {saveMsg}
            </span>
          ) : null}
        </div>
      </header>

      {queryErrors.length > 0 ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-sp-3 rounded-r-card border border-warn bg-warn-bg px-sp-3 py-sp-2 text-fs-meta text-warn-fg"
        >
          <span>
            Showing cached data for{' '}
            <span className="font-mono">{queryErrors.join(', ')}</span> —
            live fetch failed.
          </span>
          <button
            type="button"
            className="font-medium underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
            onClick={() => {
              if (scalarsQuery.isError) scalarsQuery.refetch();
              if (thumbnailsQuery.isError) thumbnailsQuery.refetch();
              if (annotationsQuery.isError) annotationsQuery.refetch();
              if (spectrogramQuery.isError) spectrogramQuery.refetch();
              if (spectrumQuery.isError) spectrumQuery.refetch();
              if (waveformQuery.isError) waveformQuery.refetch();
            }}
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Selector strip — sensor multi-select + annotations. Sits
          beneath the asset header so the operator's first scan goes
          asset → sensors → "what has the algorithm flagged?" before
          they enter the panel matrix. */}
      <section
        aria-label="Selectors"
        className="flex flex-col gap-sp-3 rounded-r-card border border-gray-300 bg-gray-50 p-sp-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-sp-3">
          <SensorChips
            sensors={data.sensors}
            active={activeSensorIds}
            onChange={setSensors}
          />
          <div className="flex flex-wrap items-center gap-sp-2">
            <CompareAssetControl
              options={options.filter((o) => o.id !== activeAssetId)}
              activeId={compareAssetId}
              onChange={setCompare}
            />
            <BaselinePicker
              captures={data.baselineCaptures.map((c) => ({
                id: c.id,
                label: c.label,
                at: new Date(c.atSec * 1000),
                note: c.note,
              }))}
              activeId={activeBaselineId}
              onSelect={setBaseline}
              onCaptureNow={captureBaselineNow}
            />
          </div>
        </div>
        <AnnotationStrip
          annotations={activeAnnotations}
          onPinMoment={() => cursor.setT(Math.round(Date.now() / 1000))}
          onSelect={(atSec) => cursor.setT(atSec)}
        />
      </section>

      <section className="flex flex-col gap-sp-3" aria-label="Signal panels">
        {/* FFT history — sits at the top of the panels section so the
            operator's eye lands here right after the selectors. The
            strip is the time-travel control for the panels below:
            picking a thumbnail re-anchors spectrum / waveform /
            baseline overlay to that capture's moment. The label
            announces *which* capture is currently driving the
            downstream panels so it's never ambiguous what the
            spectrum below is showing. */}
        <div className="flex flex-wrap items-baseline justify-between gap-sp-2">
          <SectionLabel>
            FFT history · {activeThumbnails.length} snapshots · 14d
          </SectionLabel>
          <p className="font-mono text-fs-meta text-gray-700">
            Showing{' '}
            <span className="text-gray-900">
              {selectedThumbnail
                ? selectedThumbnail.at.toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—'}
            </span>
            {selectedThumbnail ? (
              <>
                {' '}·{' '}
                <span className={TONE_TEXT[selectedThumbnail.severity]}>
                  {selectedThumbnail.severity}
                </span>
              </>
            ) : null}
            {cursor.state.t == null ? (
              <span className="text-gray-500"> · latest</span>
            ) : null}
          </p>
        </div>
        <FFTThumbnailStrip
          thumbnails={activeThumbnails}
          selectedAt={selectedThumbnail
            ? selectedThumbnail.at
            : new Date(cursor.state.t! * 1000)}
          onSelect={(at) => cursor.setT(Math.round(at.getTime() / 1000))}
        />

        {/* FFT spectrum — driven by the cursor set by the strip above. */}
        <SectionLabel>
          FFT spectrum
          {focusedFault ? (
            <>
              {' · '}
              <span className="font-mono normal-case tracking-normal text-warn-fg">
                focused on {focusedFault.label} @ {focusedFault.hz} Hz
              </span>{' '}
              <TextButton
                label="clear focus"
                onClick={() => setFocusedFault(null)}
              />
            </>
          ) : null}
        </SectionLabel>
        <SpectrumPanel
          amplitudes={liveSpectrum?.amplitudes ?? data.spectrum.amplitudes}
          maxHz={liveSpectrum?.maxHz ?? data.spectrum.maxHz}
          markers={allSpectrumMarkers}
          unit="g rms"
          cursorFraction={cursorFraction}
          focusHz={focusedFault?.hz ?? null}
          focusWindowHz={25}
          harmonicsOf={focusedFault?.hz ?? null}
          harmonicCount={4}
          showPeaks
          peakCount={5}
        />

        <SectionLabel>Current vs baseline</SectionLabel>
        <ComparisonOverlay
          baseline={data.baselineSpectrum}
          current={liveSpectrum?.amplitudes ?? data.spectrum.amplitudes}
          maxHz={liveSpectrum?.maxHz ?? data.spectrum.maxHz}
          baselineLabel="Healthy baseline"
          currentLabel="Right now"
        />

        {/* Scalars and time-domain waveform share a row. Both read
            the same LinkedCursor, so showing them side-by-side lets
            the operator correlate "what does the long-window trend
            look like" with "what does the burst at the selected
            moment look like" without scrolling between them. At <lg
            widths the grid collapses to a single column so each
            chart keeps a useful drawing area.

            `items-stretch` (CSS default) makes the columns equal
            height; the inner cards use `flex-1` so they fill that
            shared height instead of hugging their natural content
            size, which is what gives the two panels visually
            aligned bottom edges. Inside the scalars card the four
            ScalarTracks distribute the remaining space evenly via
            `flex-1` + `flex-col`, so a taller column yields taller
            sparklines instead of trailing whitespace. */}
        <div className="grid grid-cols-1 items-stretch gap-sp-3 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-sp-3">
            <SectionLabel>
              Scalars · 14d · {primarySensor.name}
              {compareData ? (
                <>
                  {' '}·{' '}
                  <span className="font-mono normal-case tracking-normal text-gray-700">
                    vs {compareData.assetName}
                  </span>
                </>
              ) : null}
            </SectionLabel>
            <div className="flex flex-1 flex-col rounded-r-card border border-gray-300 bg-gray-50">
              {activeScalars.map((s) => {
                const compareSeries = compareData?.scalars.find(
                  (cs) => cs.metric === s.metric,
                );
                return (
                  <div key={s.metric} className="flex flex-1 flex-col justify-center">
                    <ScalarTrack
                      metric={s.metric}
                      unit={s.unit}
                      points={s.points}
                      last={s.last}
                      cursorFraction={cursorFraction}
                      compare={compareSeries?.points}
                      compareLabel={compareSeries ? compareData?.assetName : undefined}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-sp-3">
            <SectionLabel>Time-domain waveform</SectionLabel>
            <div className="flex flex-1 flex-col [&>figure]:flex-1">
              <WaveformPanel
                samples={liveWaveform?.samples ?? data.waveform}
                sampleRate={liveWaveform?.sampleRate ?? data.sampleRate}
                unit="g"
                cursorFraction={cursorFraction}
              />
            </div>
          </div>
        </div>

        {/* Spectrogram + bearing-fault tracker share a row: the
            tracker rows drive the spectrum focus state, so keeping
            it visible next to the spectrogram (the time-evolution
            view) lets the operator click a fault row and see both
            the focused FFT slice + the long-window heatmap. */}
        <div className="grid grid-cols-1 gap-sp-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-sp-3">
            <SectionLabel>Spectrogram</SectionLabel>
            <SpectrogramPanel
              grid={liveSpectrogram?.grid ?? data.spectrogramGrid}
              maxHz={liveSpectrogram?.maxHz ?? data.spectrum.maxHz}
              durationSec={
                liveSpectrogram?.durationSec ?? data.spectrogramDurationSec
              }
              peakTone="critical"
              cursorFraction={cursorFraction}
            />
          </div>
          <div className="flex flex-col gap-sp-3">
            <div className="flex items-baseline justify-between">
              <SectionLabel>Bearing fault frequencies</SectionLabel>
              <TextButton
                label="Edit markers ↗"
                onClick={() => setEditorOpen(true)}
              />
            </div>
            <BearingFreqTracker
              frequencies={trackerRows}
              selectedLabel={focusedFault?.label}
              onSelect={(hz, f) => setFocusedFault({ hz, label: f.label })}
            />
          </div>
        </div>
      </section>

      <FaultFrequencyEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        assetId={activeAssetId}
        assetName={data.assetName}
        shaftRpm={Math.round((getAssetMeta(activeAssetId)?.runHz ?? 60) * 60)}
        markers={customMarkers}
        onSave={commitCustomMarkers}
      />
    </div>
  );
}

/**
 * Cursor controls: pin/step/clear. Stepping moves the cursor by the
 * provider's configured tickSeconds. Pin uses Date.now() so a fresh
 * deep-link starts at "right now" — operators can fine-tune from
 * there with the step controls.
 */
function CursorControls() {
  const cursor = useLinkedCursor();
  return (
    <div className="flex items-center gap-sp-1 rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1">
      <TextButton label="◀" onClick={() => cursor.step(-1)} />
      {cursor.state.t == null ? (
        <TextButton
          label="Pin now"
          onClick={() => cursor.setT(Math.round(Date.now() / 1000))}
        />
      ) : (
        <TextButton label="Clear" onClick={() => cursor.setT(null)} />
      )}
      <TextButton label="▶" onClick={() => cursor.step(1)} />
    </div>
  );
}

// useRouter throws when not inside RouterProvider. Catch the throw
// and return null so the screen renders in isolation (tests, Storybook).
function safeUseRouter(): ReturnType<typeof useRouter> | null {
  try {
    return useRouter();
  } catch {
    return null;
  }
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
      {children}
    </h2>
  );
}

// Asset photo thumbnail consolidated into the shared `AssetPhoto`
// component in @/lib (size="header" matches the 64px hero variant).
// The shared component remounts on src change so a transient first-
// paint 404 doesn't stick after the JPG becomes available.

/**
 * Asset picker — the prominent left-side selector from the screens.md
 * spec, minimum-viable: a chip showing the active asset, click opens
 * a popover listing every catalog asset. Picking commits + closes.
 * Outside-click + Escape dismiss the popover.
 *
 * The full picker spec (Recent / Pinned / By site tabs, search, pin
 * stars) is deferred until the matching `/v1/users/me/assets`
 * endpoint exists. This version is intentionally a fraction of that
 * surface so the operator can change asset *today* without waiting
 * on the full overlay.
 */
function AssetPicker({
  options,
  activeId,
  activeName,
  onChoose,
}: {
  options: AssetOption[];
  activeId: string;
  activeName: string;
  onChoose: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Outside-click + Escape close
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        // Picker is a control, not the screen's hero heading — sized to
        // match S1's "Also needs attention" h2 + S2's section labels so
        // the page weight feels consistent across screens.
        className="group flex items-center gap-sp-2 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-left transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
        title="Change asset"
      >
        <span className="text-fs-statement font-medium text-gray-900">{activeName}</span>
        <span className="text-fs-meta text-gray-700" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label="Choose asset to analyze"
          className="absolute left-0 z-10 mt-sp-1 w-80 rounded-r-card border border-gray-300 bg-gray-50 p-sp-1 shadow-lg"
        >
          {options.map((o) => {
            const active = o.id === activeId;
            return (
              <button
                key={o.id}
                role="option"
                aria-selected={active}
                onClick={() => {
                  onChoose(o.id);
                  setOpen(false);
                }}
                className={
                  'flex w-full items-center justify-between gap-sp-3 rounded-r-default px-sp-3 py-sp-2 text-left transition-colors focus:outline-none ' +
                  (active
                    ? 'bg-info-bg text-gray-900'
                    : 'text-gray-900 hover:bg-gray-100')
                }
              >
                <span className="min-w-0">
                  <span className="block truncate text-fs-body">{o.name}</span>
                  <span className="font-mono text-fs-meta text-gray-700">{o.id}</span>
                </span>
                <span className="shrink-0 text-fs-meta text-gray-700">{o.site}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * `+ Compare asset` control — outline button that opens a popover
 * listing the assets the operator could overlay on the scalar
 * tracks. Today it supports a single compare asset (spec allows
 * up to 3); when an asset is selected the popover button becomes
 * a "Comparing X · clear" chip so the operator can see and
 * dismiss the overlay without re-opening the menu.
 */
function CompareAssetControl({
  options,
  activeId,
  onChange,
}: {
  options: AssetOption[];
  activeId: string | null;
  onChange: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = options.find((o) => o.id === activeId) ?? null;

  if (active) {
    return (
      <div className="inline-flex items-center gap-sp-1 rounded-r-default border border-gray-900 bg-gray-50 px-sp-2 py-sp-1 text-fs-meta">
        <span className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Compare
        </span>
        <span className="text-gray-900">{active.name}</span>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-sp-1 rounded-r-chip text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
          aria-label="Clear compare"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-sp-1 rounded-r-default border border-dashed border-gray-500 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
        title="Overlay another asset on the scalar tracks"
      >
        + Compare asset
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label="Choose asset to compare"
          className="absolute right-0 z-10 mt-sp-1 w-80 rounded-r-card border border-gray-300 bg-gray-50 p-sp-1 shadow-lg"
        >
          {options.length === 0 ? (
            <p className="px-sp-3 py-sp-2 text-fs-meta text-gray-700">
              No other assets in the catalog to compare.
            </p>
          ) : (
            options.map((o) => (
              <button
                key={o.id}
                role="option"
                aria-selected={false}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between rounded-r-default px-sp-3 py-sp-2 text-left text-gray-900 hover:bg-gray-100 focus:outline-none"
              >
                <span className="min-w-0">
                  <span className="block truncate text-fs-body">{o.name}</span>
                  <span className="font-mono text-fs-meta text-gray-700">
                    {o.id} · {o.klass}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-fs-meta text-gray-700">
                  {o.site}
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Sensor multi-select chip group. The S5 spec calls for a small
 * chip-style multi-select to the right of the asset picker; toggling
 * a chip rewrites the URL `?sensors=` param via `onChange` (mutated
 * outside this component so the URL stays the source of truth).
 *
 * The chip group enforces the "at least one sensor must be selected"
 * invariant — clicking the last selected chip is a no-op rather than
 * an empty selection, because every downstream chart anchors to
 * `sensors[0]` and would crash on an empty array.
 */
function SensorChips({
  sensors,
  active,
  onChange,
}: {
  sensors: Array<{ id: string; name: string; position: string; uploadEverySec: number }>;
  active: string[];
  onChange: (next: string[]) => void;
}) {
  const activeSet = new Set(active);
  const toggle = (id: string) => {
    if (activeSet.has(id)) {
      if (activeSet.size === 1) return; // keep at least one selected
      onChange(active.filter((x) => x !== id));
    } else {
      onChange([...active, id]);
    }
  };
  // Short label per chip — drive-end / non-drive-end / casing. Falls
  // back to the last segment of the sensor id (e.g. "de" / "nde") for
  // unknown positions so a new sensor type doesn't break the chip.
  const shortLabel = (s: { id: string; position: string }) => {
    if (s.position === 'drive_end') return 'DE';
    if (s.position === 'non_drive_end') return 'NDE';
    if (s.position === 'casing') return 'CAS';
    if (s.position === 'inboard') return 'IB';
    return s.id.split('_').pop() ?? s.id;
  };
  return (
    <div role="group" aria-label="Active sensors" className="flex flex-wrap items-center gap-sp-2">
      <span className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
        Sensors
      </span>
      {sensors.map((s) => {
        const sel = activeSet.has(s.id);
        return (
          <button
            key={s.id}
            type="button"
            aria-pressed={sel}
            onClick={() => toggle(s.id)}
            title={`${s.name} · uploads every ${Math.round(s.uploadEverySec / 60)} min`}
            className={
              'inline-flex items-center gap-sp-2 rounded-r-chip border px-sp-2 py-sp-1 text-fs-meta transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
              (sel
                ? 'border-gray-900 bg-gray-900 text-gray-50'
                : 'border-gray-300 bg-gray-50 text-gray-900 hover:border-gray-500')
            }
          >
            <span className="font-mono">{s.id}</span>
            <span className="font-mono text-fs-meta opacity-80">{shortLabel(s)}</span>
          </button>
        );
      })}
      <span className="font-mono text-fs-meta text-gray-700">
        {active.length}/{sensors.length}
      </span>
    </div>
  );
}
